import { DocumentPathInfo } from '@/store/modules/fileManager/fileManagerTypes'
import { Entity, entity } from '@/store/modules/entities/entitiesTypes'
import { createEntityByType } from '@/store/modules/entities/entitiesActions'
import { TabNames } from '@/store/modules/tabs/tabsTypes'

export const getFileExtension = (fileName: string): string => {
  if (!fileName) {
    return ''
  }
  if (fileName.includes('.')) {
    return fileName.split('.').pop()?.toLowerCase() || ''
  }
  return ''
}

export const getFileDotExtension = (fileName: string): string => {
  const idx = fileName.lastIndexOf('.')
  return idx > 0 ? fileName.slice(idx).toLocaleLowerCase() : ''
}

export const formatBytes = (bytes: number, decimals: number = 2): string => {
  if (bytes === 0) {
    return '0 Bytes'
  }

  const k = 1024
  const dm = decimals < 0 ? 0 : decimals
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']

  const i = Math.floor(Math.log(bytes) / Math.log(k))
  const fileSize = parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i]
  return fileSize.replace('.', ',')
}

export const goToDocumentRoute = (document: any, pathInfo: DocumentPathInfo) => {
  const documentData = {
    entityType: document.idEntityType,
    entityId: document.idEntity,
    path: pathInfo.path,
    basePath: pathInfo.basePath,
    fileName: document.initialFile
  }
  return getEntityURL(documentData)
}

const getEntityURL = (documentData: any) => {
  const currentEntity: Entity = getEntity(Number(documentData.entityType), Number(documentData.entityId))
  const basePath = encodeURI(btoa(documentData.basePath))
  const path = encodeURI(btoa(documentData.path))
  const fileName = encodeURIComponent(documentData.fileName)

  let tab: string = ''
  switch (currentEntity.type) {
    case entity.template.type:
      tab = TabNames.TEMPLATES
      break
    case entity.general.type:
      tab = ''
      break
    default:
      tab = TabNames.DOCUMENTS
      break
  }

  return currentEntity.getRoute({ basePath, path, tab, fileName })
}

const getEntity = (entityType: number, entityId: number = 0): Entity => {
  return createEntityByType(entityType, entityId)
}

export const createNewFile = (file: File, filename: string): File => {
  return new File([file], filename, {
    type: file.type,
    lastModified: Date.now()
  })
}

const escapeRegExp = (str: string): string => {
  return str.replace(/[.*+?^=!:${}()|\[\]\/\\]/g, '\\$&')
}

export const getUniqueFileName = (existingFiles: string[], filename: string): string => {
  const baseName = filename.replace(/(\.[^.]+)$/, '')
  const extensionMatch = filename.match(/(\.[^.]+)$/)
  const extension = extensionMatch ? extensionMatch[1] : ''

  const baseRegex = new RegExp(`^${escapeRegExp(baseName)}${extension}$`, 'i')
  const baseMatches = existingFiles.filter((file) => baseRegex.test(file))

  if (baseMatches.length === 0) {
    return filename
  }

  const regex = new RegExp(`^${escapeRegExp(baseName)}( - copia_(\\d+))?${extension}$`, 'i')
  const matches = existingFiles.filter((file) => regex.test(file))

  let maxNumber = 0

  matches.forEach((file) => {
    const match = file.match(/ - copia_(\d+)/)
    if (match && match[1]) {
      maxNumber = Math.max(maxNumber, parseInt(match[1], 10))
    }
  })

  const newFileIndex = maxNumber + 1
  return `${baseName} - copia_${newFileIndex}${extension}`
}

export const checkDuplicateFile = (existingFiles: string[], newFiles: string[]): boolean => {
  return newFiles.some((newFile) => existingFiles.includes(newFile))
}

/**
 * Obtiene el nombre del archivo sin la extensión
 * @param fileName Nombre del archivo
 * @returns Nombre sin extensión
 */
export const getFileNameWithoutExtension = (fileName: string): string => {
  if (!fileName) return ''
  const lastDotIndex = fileName.lastIndexOf('.')
  return lastDotIndex !== -1 ? fileName.substring(0, lastDotIndex) : fileName
}

/**
 * Trunca un nombre de archivo si excede la longitud máxima
 * @param fileName Nombre del archivo
 * @param maxLength Longitud máxima permitida
 * @returns Nombre truncado con puntos suspensivos si es necesario
 */
export const truncateFileName = (fileName: string, maxLength: number = 27): string => {
  if (!fileName) return ''
  const name = getFileNameWithoutExtension(fileName)
  if (name.length <= maxLength) return name
  return `${name.substring(0, maxLength)}...`
}

/**
 * Mapea la extensión de un archivo a un icono correspondiente
 * @param fileName Nombre del archivo
 * @returns Clase CSS del icono correspondiente
 */
export const getFileIcon = (fileName: string): string => {
  const extension = getFileExtension(fileName)

  // Mapeo de extensiones a clases de iconos
  const extensionIconMap: Record<string, string> = {
    txt: 'e-fe-txt e-fe-icon',
    docx: 'e-fe-docx e-fe-icon',
    doc: 'e-fe-doc e-fe-icon',
    pdf: 'e-fe-pdf e-fe-icon',
    xlsx: 'e-fe-xlsx e-fe-icon',
    xls: 'e-fe-xls e-fe-icon',
    pptx: 'e-fe-pptx e-fe-icon',
    ppt: 'e-fe-ppt e-fe-icon',
    eml: 'e-fe-eml e-fe-icon',
    msg: 'e-fe-msg e-fe-icon',
    jpg: 'e-fe-jpg e-fe-icon',
    jpeg: 'e-fe-jpeg e-fe-icon',
    png: 'e-fe-png e-fe-icon',
    gif: 'e-fe-gif e-fe-icon',
    zip: 'e-fe-zip e-fe-icon',
    rar: 'e-fe-rar e-fe-icon',
    html: 'e-fe-html e-fe-icon',
    htm: 'e-fe-htm e-fe-icon',
    csv: 'e-fe-csv e-fe-icon',
    rtf: 'e-fe-rtf e-fe-icon',
    xml: 'e-fe-xml e-fe-icon',
    mp4: 'e-fe-mp4 e-fe-icon',
    mp3: 'e-fe-mp3 e-fe-icon'
  }

  return extensionIconMap[extension] || 'e-fe-txt e-fe-icon'
}
